import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  ZfAccordion,
  ZfAccordionItem,
  ZfBox,
  ZfDivider,
  ZfDrawer,
  ZfDrawerContent,
  ZfDrawerHeader,
  ZfIcon,
  ZfLink,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';
import { setIsDrawerOpen } from 'reducers/drawerReducer';
import { constructMailto } from 'utils/utils';
import { GlobalState } from 'reducers/stateTypes';
import { GravityToggle } from 'components/Navigation/GravityToggle';
import { EmailDragAndDrop } from 'components/Common/EmailDragAndDrop';

const CLM_IMPROVEMENT_EMAIL = 'clmimprovement@juliusbaer.com';
const CLM_IMPROVEMENT_EMAIL_SUBJECT = 'CLM Feedback';
const JB_SNOW_URL = 'https://servicenow.juliusbaer.com/esc';
const CLM_USER_GUIDE_URL = 'https://wiki.juliusbaer.com/spaces/viewspace.action?key=clmuserguide';

type DrawerProps = {
  anchor?: 'parent' | 'body';
};

export const Drawer = ({ anchor = 'parent' }: DrawerProps) => {
  const isDrawerOpen = useSelector((state: GlobalState) => state.drawer.isDrawerOpen);
  const dispatch = useDispatch();

  const { t } = useTranslation();

  return (
    <div>
      <ZfDrawer
        show={isDrawerOpen}
        close={async () => {
          dispatch(setIsDrawerOpen(false));
        }}
        anchor={anchor}
        side="right"
      >
        <ZfDrawerHeader showCloseButton={true} expanded={true}>
          {t('drawer.title')}
        </ZfDrawerHeader>
        <ZfDrawerContent>
          <div
            style={{
              width: '380px',
            }}
          >
            <ZfAccordion
              type="multiple"
              style={{
                margin: '-24px',
              }}
            >
              <div>
                <ZfAccordionItem
                  style={{ '--content-padding': 0 } as any}
                  showUnderline
                  titleText={t('drawer.section.contactNoteUpload.title')}
                  state="active"
                >
                  <ZfIcon name="description" size="large" slot="leading" />
                  <ZfBox style={{ padding: 'var(--padding-horizontal-md)' }}>
                    <EmailDragAndDrop />
                  </ZfBox>
                  <ZfDivider />
                </ZfAccordionItem>
                <ZfAccordionItem
                  style={{ '--content-padding': 0 } as any}
                  showUnderline={true}
                  titleText={t('drawer.section.bugInformationOverlay.title')}
                  state="active"
                >
                  <ZfIcon name="help" size="large" slot="leading" />
                  <ZfStack
                    style={{
                      padding: 'var(--padding-horizontal-md)',
                    }}
                    direction="column"
                    spacing="medium"
                  >
                    <ZfTypography>{t('drawer.section.bugInformationOverlay.text')}</ZfTypography>
                    <GravityToggle />
                  </ZfStack>
                  <ZfDivider />
                </ZfAccordionItem>
                <ZfAccordionItem
                  style={{ '--content-padding': 0 } as any}
                  showUnderline={true}
                  titleText={t('drawer.section.support.title')}
                  state="active"
                >
                  <ZfIcon name="construction" size="large" slot="leading" />
                  <ZfStack style={{ padding: 'var(--padding-horizontal-md)' }} direction="column" spacing="medium">
                    <ZfStack direction="column" spacing="small">
                      <ZfTypography variant="label-medium" weight="bold">
                        {t('drawer.section.support.text')}
                      </ZfTypography>
                      <ZfStack direction="row" spacing="extra-extra-small">
                        <ZfIcon name="call" size="large" />
                        <ZfTypography>{t('drawer.section.support.tel')}</ZfTypography>
                      </ZfStack>
                    </ZfStack>
                    <ZfStack direction="column" spacing="small">
                      <ZfTypography variant="label-medium" weight="bold">
                        {t('drawer.section.support.bugReporting')}
                      </ZfTypography>
                      <ZfTypography>{t('drawer.section.support.bugReporting.text')}</ZfTypography>
                      <ZfLink href={JB_SNOW_URL} trailingIcon="open_in_new" target="_blank" type="inline">
                        {t('drawer.support.snow')}
                      </ZfLink>
                    </ZfStack>
                  </ZfStack>
                  <ZfDivider />
                </ZfAccordionItem>
                <ZfAccordionItem
                  style={{ '--content-padding': 0 } as any}
                  showUnderline={true}
                  titleText={t('drawer.section.feedback.title')}
                  state="active"
                >
                  <ZfIcon name="mail" size="large" slot="leading" />
                  <ZfStack style={{ padding: 'var(--padding-horizontal-md)' }} direction="column" spacing="medium">
                    <ZfTypography>{t('drawer.section.feedback.text')}</ZfTypography>
                    <div>
                      <ZfTextButton
                        leadingIcon="mail"
                        hierarchy="secondary"
                        size="medium"
                        onClick={() => constructMailto(CLM_IMPROVEMENT_EMAIL, CLM_IMPROVEMENT_EMAIL_SUBJECT)}
                      >
                        {t('drawer.section.feedback.button')}
                      </ZfTextButton>
                    </div>
                  </ZfStack>
                  <ZfDivider />
                </ZfAccordionItem>
                <ZfAccordionItem
                  style={{ '--content-padding': 0 } as any}
                  showUnderline={true}
                  titleText={t('drawer.section.additionalResources.title')}
                  state="active"
                >
                  <ZfIcon name="keep" size="large" slot="leading" />
                  <ZfStack style={{ padding: 'var(--padding-horizontal-md)' }} direction="column" spacing="small">
                    <ZfTypography variant="label-medium" weight="bold">
                      {t('drawer.section.additionalResources.clmUserGuide')}
                    </ZfTypography>
                    <ZfLink type="inline" trailingIcon="open_in_new" href={CLM_USER_GUIDE_URL} target="_blank">
                      {t('drawer.section.additionalResources.clmUserGuide.link')}
                    </ZfLink>
                  </ZfStack>
                  <ZfDivider />
                </ZfAccordionItem>
              </div>
            </ZfAccordion>
          </div>
        </ZfDrawerContent>
      </ZfDrawer>
    </div>
  );
};
